import { INCLUDE_EXERCISES } from '@/constants/app-variables'
import { ONE_MINUTE_TIMESTAMP } from '@/components/patient-stats/graphs/mapping-chart-options/constants'

// Suppress any of the same type of event (For the same patient) if they come through within 10 minutes of each other
export const LIVE_EVENT_MIN_TIME_BETWEEN_ALERTS = 10 * ONE_MINUTE_TIMESTAMP

export const enum PatientStatsPageType {
  DEFAULT = 'DEFAULT',
  MULTI_DEV_DASH = 'MULTI_DEV_DASH',
  MULTI_DEV_LIMITED_DASH = 'MULTI_DEV_LIMITED_DASH',
  ACTIVITY_DASH = 'ACTIVITY_DASH',
  NURSING_HOME_SUMMARY_DASH = 'NURSING_HOME_SUMMARY_DASH',
}

export const enum SummaryReportChartKeys {
  StepCounts = 'StepCounts',
  Adherence = 'Adherence',
  ShortnessOfBreath = 'ShortnessOfBreath',
  NumberOfAwakenings = 'NumberOfAwakenings',
  MaintenanceInhalerUsage = 'MaintenanceInhalerUsage',
  RescueInhalerUsage = 'RescueInhalerUsage',
  BodyWeight = 'BodyWeight',
  BodyTemperature = 'BodyTemperature',
  OxygenSaturation = 'OxygenSaturation',
  HeartRate = 'HeartRate',
  PerfusionIndex = 'PerfusionIndex2',
  BloodPressureSystolic = 'BloodPressureSystolic',
  BloodPressureDiastolic = 'BloodPressureDiastolic',
}

export const enum PatientStatsTabKeys {
  Summary = 'summary',
  Mapping = 'mapping',
  Detailed = 'detailed',
  Exercise = 'exercise',
  Notes = 'notes',
  Actigraphy = 'actigraphy',
}

export type PatientStatsTab = { key: PatientStatsTabKeys; label: string }
export const PATIENT_STATS_PAGE_TAB: Record<string, PatientStatsTab> = {
  SUMMARY: { key: PatientStatsTabKeys.Summary, label: 'SUMMARY' },
  MAPPING: { key: PatientStatsTabKeys.Mapping, label: 'MAPPING' },
  DETAILED: { key: PatientStatsTabKeys.Detailed, label: 'DETAILED' },
  EXERCISE: INCLUDE_EXERCISES ? { key: PatientStatsTabKeys.Exercise, label: 'EXERCISE' } : null,
  NOTES: { key: PatientStatsTabKeys.Notes, label: 'NOTES' },
  ACTIGRAPHY: { key: PatientStatsTabKeys.Actigraphy, label: 'ACTIGRAPHY' },
}

export type PatientStatsPageConfig = {
  showTimeline: boolean
  defaultDisplayWeeks: number
  columns: number
  tabs: PatientStatsTab[]
  summaryReportsChartKeys: SummaryReportChartKeys[]
}

export type PatientStatsPageConfigs = Record<PatientStatsPageType, PatientStatsPageConfig>

export const PATIENT_STATS_PAGE_CONFIGS: PatientStatsPageConfigs = {
  [PatientStatsPageType.DEFAULT]: {
    showTimeline: true,
    defaultDisplayWeeks: 6,
    columns: 3,
    tabs: Object.values(PATIENT_STATS_PAGE_TAB).filter(x => !!x), // filter not null
    summaryReportsChartKeys: [
      SummaryReportChartKeys.StepCounts,
      SummaryReportChartKeys.Adherence,
      SummaryReportChartKeys.ShortnessOfBreath,
      SummaryReportChartKeys.NumberOfAwakenings,
      SummaryReportChartKeys.MaintenanceInhalerUsage,
      SummaryReportChartKeys.RescueInhalerUsage,
    ],
  },
  [PatientStatsPageType.MULTI_DEV_DASH]: {
    showTimeline: true,
    defaultDisplayWeeks: 6,
    columns: 3,
    tabs: Object.values(PATIENT_STATS_PAGE_TAB).filter(x => !!x), // filter not null
    summaryReportsChartKeys: [
      SummaryReportChartKeys.StepCounts,
      SummaryReportChartKeys.BodyWeight,
      SummaryReportChartKeys.MaintenanceInhalerUsage,
      SummaryReportChartKeys.NumberOfAwakenings,
      SummaryReportChartKeys.BodyTemperature,
      SummaryReportChartKeys.RescueInhalerUsage,
      SummaryReportChartKeys.OxygenSaturation,
      SummaryReportChartKeys.HeartRate,
      SummaryReportChartKeys.PerfusionIndex,
      SummaryReportChartKeys.BloodPressureSystolic,
      SummaryReportChartKeys.BloodPressureDiastolic,
    ],
  },
  [PatientStatsPageType.MULTI_DEV_LIMITED_DASH]: {
    showTimeline: true,
    defaultDisplayWeeks: 6,
    columns: 3,
    tabs: Object.values(PATIENT_STATS_PAGE_TAB).filter(x => !!x), // filter not null
    summaryReportsChartKeys: [
      SummaryReportChartKeys.StepCounts,
      SummaryReportChartKeys.BodyWeight,
      SummaryReportChartKeys.MaintenanceInhalerUsage,
      SummaryReportChartKeys.NumberOfAwakenings,
      SummaryReportChartKeys.BodyTemperature,
      SummaryReportChartKeys.RescueInhalerUsage,
    ],
  },
  [PatientStatsPageType.ACTIVITY_DASH]: {
    showTimeline: true,
    defaultDisplayWeeks: 6,
    columns: 3,
    tabs: Object.values(PATIENT_STATS_PAGE_TAB).filter(x => !!x), // filter not null
    summaryReportsChartKeys: [SummaryReportChartKeys.StepCounts, SummaryReportChartKeys.NumberOfAwakenings],
  },
  [PatientStatsPageType.NURSING_HOME_SUMMARY_DASH]: {
    showTimeline: true,
    defaultDisplayWeeks: 2,
    columns: 2,
    tabs: Object.values(PATIENT_STATS_PAGE_TAB).filter(x => !!x), // filter not null
    summaryReportsChartKeys: [
      SummaryReportChartKeys.StepCounts,
      SummaryReportChartKeys.NumberOfAwakenings,
      SummaryReportChartKeys.OxygenSaturation,
      SummaryReportChartKeys.HeartRate,
      SummaryReportChartKeys.BodyWeight,
      SummaryReportChartKeys.BodyTemperature,
    ],
  },
}
